export const serviceEditorial: Record<
  string,
  {
    caption: string;
    introduction: string;
    scope: string[];
    details: string[];
    applications: { title: string; copy: string }[];
    faqs: { question: string; answer: string }[];
  }
> = {
  "product-development": {
    caption: "A system shaped around you.",
    introduction:
      "There is no template for your ambition. We connect product thinking, considered design and careful engineering to make software that belongs to your business.",
    scope: ["Business platforms", "Digital products", "Internal tools"],
    details: [
      "Define the problem, understand the people and give the first release a clear purpose.",
      "Map the important journeys and turn complex workflows into intuitive experiences.",
      "Build connected interfaces and dependable application logic as one coherent product.",
      "Connect the systems you already rely on, with clear boundaries and thoughtful data flows.",
      "Check critical journeys, handle edge cases and prepare a considered release.",
      "Use feedback to prioritise improvements and support the next chapter of the product.",
    ],
    applications: [
      {
        title: "Operations, made clearer.",
        copy: "Bring scattered workflows into one platform shaped around the way your team works.",
      },
      {
        title: "An idea, made tangible.",
        copy: "Turn a new product concept into a focused first release that people can use and learn from.",
      },
      {
        title: "A platform, ready to grow.",
        copy: "Evolve existing software with better experiences, cleaner foundations and useful integrations.",
      },
    ],
    faqs: [
      {
        question: "Can we start with a small first release?",
        answer:
          "Yes. We can define a focused first version around the most important user journey, then use what you learn to guide the next release.",
      },
      {
        question: "Can you improve software we already have?",
        answer:
          "We start by understanding the current product and its constraints. That helps us decide what to keep, what to improve and where a rebuild may make sense.",
      },
      {
        question: "How do you define the scope and timeline?",
        answer:
          "We discuss your goals, users, integrations and priorities before proposing an approach. The scope and delivery plan follow from that conversation.",
      },
    ],
  },
  "web-development": {
    caption: "A considered experience, everywhere.",
    introduction:
      "The web is often where your customer meets your business. We make that experience clear, distinctive and dependable, across screens and throughout the journey.",
    scope: ["SaaS products", "Customer portals", "Business websites"],
    details: [
      "Build interfaces that adapt naturally to different screens and input methods.",
      "Connect onboarding, core workflows and administration in a coherent product.",
      "Create reusable components that keep the experience consistent as it grows.",
      "Bring payments, business systems and external services into the right places.",
      "Consider loading, keyboard navigation and usability throughout the build.",
      "Give your team practical ways to manage content and everyday operations.",
    ],
    applications: [
      {
        title: "A distinctive first impression.",
        copy: "A business website that expresses your brand and guides visitors towards a meaningful next step.",
      },
      {
        title: "A product for everyday use.",
        copy: "A SaaS experience with clear workflows, considered onboarding and room to evolve.",
      },
      {
        title: "A better way to connect.",
        copy: "Customer and partner portals that make information, requests and services easier to access.",
      },
    ],
    faqs: [
      {
        question: "Do you design and develop the whole experience?",
        answer:
          "Yes. We can bring product direction, interface design and development together, or work with your existing design and engineering team.",
      },
      {
        question: "Will the platform work well on mobile?",
        answer:
          "Responsive behaviour is part of the design and build. We consider smaller screens, touch interactions and the tasks people need to complete on the move.",
      },
      {
        question: "Can our team manage content after launch?",
        answer:
          "We can include content and administration tools in the scope. We choose an approach around what your team needs to update and how often.",
      },
    ],
  },
  "app-development": {
    caption: "Thoughtful software, within reach.",
    introduction:
      "A great app earns its place in someone’s day. We focus on the moments that matter, shaping mobile experiences that feel natural from the first tap.",
    scope: ["iOS & Android", "Customer apps", "Team mobility"],
    details: [
      "Plan and build for the platforms your audience actually uses.",
      "Share the right foundations across platforms while respecting mobile conventions.",
      "Explore key journeys with interactive prototypes before committing to the build.",
      "Connect the app to the services, accounts and data it needs.",
      "Design useful notifications and resilient workflows for changing connectivity.",
      "Test on relevant devices and prepare for the platform release process.",
    ],
    applications: [
      {
        title: "Services, closer to people.",
        copy: "Make booking, buying or requesting a service easier with a focused mobile experience.",
      },
      {
        title: "Work, beyond the desk.",
        copy: "Give field teams the information and workflows they need, wherever the day takes them.",
      },
      {
        title: "A useful daily habit.",
        copy: "Build a customer product around repeated value, clear interactions and thoughtful engagement.",
      },
    ],
    faqs: [
      {
        question: "Can you build for both iOS and Android?",
        answer:
          "Yes. We discuss your users, device requirements and product needs to choose an appropriate platform strategy.",
      },
      {
        question: "Can an app connect to our existing platform?",
        answer:
          "We review your APIs and data flows first, then plan how the app should communicate with the existing systems and where new services are needed.",
      },
      {
        question: "Do you help with app releases?",
        answer:
          "Release preparation and support can be included in the project. App store approval depends on the relevant platform’s review process.",
      },
    ],
  },
  "ai-transformation": {
    caption: "Intelligence with a clear purpose.",
    introduction:
      "Useful AI starts with a real need. We help identify the repetitive work, difficult searches and slow decisions where carefully designed intelligence can make a difference.",
    scope: ["Workflow automation", "Knowledge tools", "Intelligent features"],
    details: [
      "Find practical opportunities and assess the data, effort and value involved.",
      "Connect repeatable tasks and business systems into clearer workflows.",
      "Embed useful assistance into the experience rather than adding complexity.",
      "Design conversational tools around defined tasks and appropriate boundaries.",
      "Make relevant information easier to retrieve from documents and knowledge sources.",
      "Connect to your systems and evaluate behaviour against meaningful examples.",
    ],
    applications: [
      {
        title: "Less repetitive work.",
        copy: "Automate predictable steps while keeping the right human review points in the workflow.",
      },
      {
        title: "Knowledge, easier to find.",
        copy: "Help teams navigate documents and internal information with useful retrieval and assistance.",
      },
      {
        title: "A more helpful product.",
        copy: "Add intelligent features that support a clear user task and can be evaluated in context.",
      },
    ],
    faqs: [
      {
        question: "Where should we start with AI?",
        answer:
          "Start with a specific workflow or user problem. We assess whether AI is a suitable approach and what data, integrations and review steps it needs.",
      },
      {
        question: "Can people stay in control of the workflow?",
        answer:
          "Yes. We can design review steps, approval points and fallback paths around the consequences of each task.",
      },
      {
        question: "Can you work with our existing tools and data?",
        answer:
          "We review the systems, access requirements and data involved before proposing the integration. The approach depends on your use case and constraints.",
      },
    ],
  },
  "cloud-solutions": {
    caption: "Strong foundations. Quiet confidence.",
    introduction:
      "The best infrastructure gives your product room to do its work. We bring clarity to architecture, delivery and operations so your team can focus on what comes next.",
    scope: ["Cloud architecture", "Delivery systems", "Product reliability"],
    details: [
      "Plan an environment around the product’s needs and migrate with care.",
      "Make build and release steps repeatable and easier to understand.",
      "Bring relevant signals together so your team can investigate issues.",
      "Define infrastructure consistently and reduce repetitive setup work.",
      "Review bottlenecks and plan appropriate capacity as demand changes.",
      "Consider access, recovery and dependable operation as part of the foundation.",
    ],
    applications: [
      {
        title: "A considered starting point.",
        copy: "Set up cloud foundations for a new product with a clear route from development to production.",
      },
      {
        title: "A smoother path to release.",
        copy: "Improve deployment workflows so the team can ship changes with greater consistency.",
      },
      {
        title: "Clarity as you grow.",
        copy: "Review an existing environment and prioritise improvements in visibility, performance and reliability.",
      },
    ],
    faqs: [
      {
        question: "Can you improve our existing cloud setup?",
        answer:
          "Yes. We begin with the architecture, deployment process and operational pain points, then recommend a practical sequence of improvements.",
      },
      {
        question: "Do we need to migrate everything at once?",
        answer:
          "Not necessarily. We can plan phased changes around dependencies, service continuity and the time your team has available.",
      },
      {
        question: "How do you approach infrastructure costs?",
        answer:
          "We consider the product’s requirements and usage patterns when making architecture choices. Reviewing capacity and unnecessary resources can be part of the work.",
      },
    ],
  },
};
